import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { message, Modal } from "antd";

import { generateRandomString } from "utils/helper";
import { initialValues as INITIALVALUES } from "../schema/userRoleSchema";
import * as services from "../services/rolePermissionApi";
import {
  setDropdownRole,
  setIsLoading,
  setRoleList,
} from "../slices/userSlice";

function toInitialValues(initialValues = {}) {
  if (initialValues.roleId) {
    return {
      ...initialValues,
      roleName: initialValues.roleName,
      roleNameEn: initialValues.roleNameEn,
    };
  }
  return INITIALVALUES;
}

function toParams(params = {}) {


  const { pagination = {}, sortBy = [] } = params;
  const modifiedParams = {
    ...params,
    pagination: {
      page: pagination.page + 1,
      size: pagination.pageSize,
    },
    sortBy: sortBy
      .filter((s) => s.order !== undefined)
      .map((s) => ({
        direction: s.order === "descend" ? "desc" : "asc",
        property: s.field,
      })),
  };
  console.log('params', modifiedParams);
  return modifiedParams;
}

function useUserRole() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { currentCompany, currentEmployee } = useSelector((state) => state.company);
  const isLoading = useSelector((state) => state.user.isLoading);
  const roleList = useSelector((state) => state.user.roleList);
  const dropdownRole = useSelector((state) => state.user.dropdownRole);

  const [pagination, setPagination] = useState({ page: 1, size: 10 });
  const [openModal, setOpenModal] = useState(false);
  const [currentRole, setRole] = useState({});

  const initialValues = useMemo(() => {
    return toInitialValues(currentRole);
  }, [currentRole]);

  const roleOptions = useMemo(() => {
    return dropdownRole.map((opt) => ({
      ...opt,
      label: i18n.language === "en" ? opt.roleNameEn : opt.roleName,
      value: opt.roleCode,
    }));
  }, [dropdownRole, i18n.language]);

  const closeModal = useCallback(() => {
    setOpenModal(false);
    setRole({});
  }, []);

  const getUserRoleList = useCallback(
    async (params = {}) => {
      if (currentCompany)
        try {
          dispatch(setIsLoading(true));
          const response = await services.getAllRole({
            requestId: generateRandomString(),
            companyId: currentCompany,
            ...params.pagination,
            sortBy: params?.sortBy,
          });

          // console.log("Response ", response);

          dispatch(setRoleList(response?.data?.page?.content || []));
          setPagination({
            ...pagination,
            total: response?.data?.page?.totalElements,
            current: response?.data?.page?.number + 1,
            page: response?.data?.page?.number + 1,
          });
          console.log('pagination11', pagination);

        } catch (error) {
          message.error(t("common.error"))
        } finally {
          dispatch(setIsLoading(false));
        }
    },
    [currentCompany, pagination, dispatch]
  );
  const onSearch = useCallback(
    async (params = {}) => {
      if (currentCompany)
        try {
          dispatch(setIsLoading(true));
          const response = await services.getAllRole({
            requestId: generateRandomString(),
            ...params,
            companyId: currentCompany,
            // ...params.pagination,
            ...pagination,
            sortBy: params.sortBy,
            page: 0,
            pageNo: 0
          });

          dispatch(setRoleList(response?.data?.page?.content || []));
          setPagination({
            ...pagination,
            total: response?.data?.page?.totalElements,
            current: response?.data?.page?.number + 1,
            page: response?.data?.page?.number + 1,
          });
          console.log('pagination12', pagination);
        } catch (error) {
          message.error(t("common.error"))
        } finally {
          dispatch(setIsLoading(false));
        }
    },
    [currentCompany, pagination, dispatch]
  );

  const getDropdownRole = useCallback(async () => {
    if (currentCompany)
      try {
        dispatch(setIsLoading(true));
        const response = await services.getDropdownRoleDefault({
          requestId: generateRandomString(),
          companyId: currentCompany,
        });
        dispatch(setDropdownRole(response.data || []));
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
  }, [currentCompany, dispatch]);

  const onCreate = useCallback(() => {
    setOpenModal(true);
    setRole({});
  }, [setOpenModal]);

  const onEdit = useCallback(
    async (values = {}) => {
      try {
        dispatch(setIsLoading(true));
        // const response = await services.getRoleById({
        //   requestId: generateRandomString(),
        //   companyId: currentCompany,
        //   roleId: values.roleId,
        // });
        setRole({ ...values });
        setOpenModal(true);
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch]
  );



  const onPermissionSetting = useCallback(
    (values = {}) => {
      if (values.roleId) {
        navigate(`/user_management/permission?roleId=${values.roleId}`);
      }
    },
    [navigate]
  );

  const createRole = useCallback(
    async (values = {}) => {
      try {
        dispatch(setIsLoading(true));
        await services.createRole(
          {
            ...values,
            requestId: generateRandomString(),
            companyId: currentCompany,
            actionId: currentEmployee
          }
        );
        message.success(t("common.success"))
        closeModal();
        getUserRoleList({ pagination: { page: 0, size: 10 } });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch, closeModal, getUserRoleList]
  );

  const updateRole = useCallback(
    async (values = {}) => {
      try {
        dispatch(setIsLoading(true));
        await services.updateRole(
          toParams({
            ...values,
          })
        );
        message.success(t("common.success"))
        closeModal();
        getUserRoleList({ pagination: { page: 0, size: 10 } });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [pagination, currentCompany, dispatch, closeModal, getUserRoleList]
  );

  const onStatus = useCallback(
    async (val, record) => {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (i18n.language === "en"
          ? <span>Are you sure confirm to change status ?</span>
          : <span>คุณยืนยันการเปลี่ยนสถานะใช่หรือไม่ ?</span>
        ),
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
        onOk: async () => {
          try {
            dispatch(setIsLoading(true));
            await services.manageRoleStatus({
              ...toParams({
                requestId: generateRandomString(),
                companyId: currentCompany,
                roleId: record.roleId,
                statusFlag: val ? "ACTIVE" : "INACTIVE",
              }),
              // requestId: generateRandomString(),
              // companyId: currentCompany,
              // roleId: record.roleId,
              // statusFlag: val == true ? "ACTIVE" : "INACTIVE",
            }
            );
            message.success(t("common.success"))
            await getUserRoleList({ pagination: { page: pagination.page - 1, size: pagination.size } });
          } catch (error) {
            message.error(t("common.error"))
          } finally {
            dispatch(setIsLoading(false));
          }
        }
      })

    },
    [pagination, currentCompany, dispatch, getUserRoleList]
  );

  const onSubmit = useCallback(
    (values = {}) => {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (
          <> {i18n.language == "en" ?
            <p>Are you sure confirm to {values.roleId ? "edit" : "create"} ?</p>
            : <p>คุณแน่ใจหรือไม่ที่จะ {values.roleId ? "แก้ไข" : "สร้างใหม่"} ?</p>}
          </>
        ),
        onOk() {
          if (values.roleId) {
            updateRole(values);
          } else {
            createRole(values);
          }
        },
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
      });
    },
    [t, createRole, updateRole]
  );

  const onCancel = useCallback(() => {
    Modal.confirm({
      title: t("dialog.confirmation.header"),
      content: (
        <>
          <p>{t('are-you-sure-confirm-to-cancel')}</p>
        </>
      ),
      onOk() {
        setOpenModal(false);
        setRole({});
      },
      okText: t("common.confirm"),
      cancelText: t("common.cancel"),
    });
  }, [t]);

  const onChange = useCallback((tablePagination, tableFilters, tableSorter, tableExtra) => {
    const modifiedSorter = Array.isArray(tableSorter)
      ? [...tableSorter]
      : [tableSorter];

    const modifiedParams = toParams({
      pagination: {
        page: tablePagination.current - 2,
        pageSize: tablePagination.pageSize,
        current: tablePagination.current - 2,
      },
      sortBy: modifiedSorter,
    });
    setPagination({ ...modifiedParams?.pagination })
    console.log('pagination13', pagination);
    getUserRoleList({ ...modifiedParams, request: pagination.request });
  },
    [getUserRoleList, pagination])


  useEffect(() => {
    if (typeof currentCompany !== "object" && currentCompany !== null) {
      getUserRoleList({ pagination: { page: 0, size: 10 } });
    }
  }, [currentCompany]);

  useEffect(() => {
    if (typeof currentCompany !== "object" && currentCompany !== null) {
      getDropdownRole();
    }
  }, [currentCompany, getDropdownRole]);

  return {
    roleList,
    isLoading,
    openModal,
    pagination,
    roleOptions,
    initialValues,
    onEdit,
    onCreate,
    onSubmit,
    onCancel,
    onStatus,
    onSearch,
    onPermissionSetting,
    onChange
  };
}

export default useUserRole;
